import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ExternalLink, Calendar, Scale, Landmark, Layers3, FileText, BookOpen } from 'lucide-react';
import { LegalResolution } from '@/services/legalSearchService';

interface LegalSearchResultsProps {
  results: LegalResolution[];
  selectedResolutions: LegalResolution[];
  onToggleSelect: (resolution: LegalResolution) => void;
}

export const LegalSearchResults: React.FC<LegalSearchResultsProps> = ({
  results,
  selectedResolutions,
  onToggleSelect,
}) => {
  const isSelected = (res: LegalResolution) => {
    return selectedResolutions.some(
      (selected) => selected.number === res.number && selected.type === res.type
    );
  };

  const getSourceStyle = (source: string) => {
    switch (source) {
      case 'CENDOJ':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800';
      case 'TEAC':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800';
      case 'DGT':
      case 'HACIENDA':
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800';
      case 'JCCA_EXTREMADURA':
        return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800';
      case 'CCCP_ANDALUCIA':
        return 'bg-lime-100 text-lime-800 border-lime-200 dark:bg-lime-900/40 dark:text-lime-300 dark:border-lime-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const groupedResults = results.reduce<Record<string, LegalResolution[]>>((acc, res) => {
    const source = res.source || res.type || 'DGT';
    if (!acc[source]) acc[source] = [];
    acc[source].push(res);
    return acc;
  }, {});

  const sourceOrder = ['CENDOJ', 'TEAC', 'DGT', 'HACIENDA', 'JCCA_EXTREMADURA', 'CCCP_ANDALUCIA'];
  const sourceLabels: Record<string, string> = {
    JCCA_EXTREMADURA: 'Junta Consultiva de Extremadura',
    CCCP_ANDALUCIA: 'Comisión Consultiva de Andalucía',
  };
  const remainingSources = Object.keys(groupedResults).filter((source) => !sourceOrder.includes(source));

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Scale className="h-3.5 w-3.5" /> Resoluciones Encontradas
        </span>
        <span className="text-xs text-muted-foreground">
          Selecciona para comparar o resumir
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[...sourceOrder.filter((source) => groupedResults[source]?.length), ...remainingSources].map((source) => (
          <div key={source} className="md:col-span-2 space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Layers3 className="h-3.5 w-3.5" />
              <span>{sourceLabels[source] || source}</span>
              <Badge variant="outline" className={getSourceStyle(source)}>
                {groupedResults[source].length} resultados
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groupedResults[source].map((res, idx) => {
                const checked = isSelected(res);
                const uniqueId = `resolution-${res.source || res.type}-${res.number}-${idx}`;
                const resolutionUrl = res.officialUrl || res.link;
                const hechosText = res.hechos || res.summary;
                const antecedentesText = res.antecedentesJuridicos || res.fundamentos;

                return (
                  <Card
                    key={uniqueId}
                    className={`p-4 border transition-all duration-200 relative overflow-hidden flex flex-col justify-between ${
                      checked
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'hover:border-primary/40 hover:shadow-sm'
                    }`}
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline" className={getSourceStyle(res.source || res.type)}>
                            {res.source || res.type}
                          </Badge>
                          {res.kind && (
                            <Badge variant="outline" className="bg-muted/50 text-muted-foreground border-border">
                              {res.kind}
                            </Badge>
                          )}
                          {res.corpusOrigin && (
                            <Badge variant="outline" className="bg-background text-muted-foreground border-dashed">
                              {res.corpusOrigin}
                            </Badge>
                          )}
                          <span className="text-xs font-mono text-muted-foreground truncate max-w-[150px]" title={res.number}>
                            {res.number}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor={uniqueId}
                            className="text-xs text-muted-foreground cursor-pointer select-none hover:text-primary transition-colors"
                          >
                            Comparar
                          </label>
                          <Checkbox
                            id={uniqueId}
                            checked={checked}
                            onCheckedChange={() => onToggleSelect(res)}
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-sm leading-snug line-clamp-2" title={res.title}>
                          {res.title}
                        </h4>
                      </div>

                      {/* Resumen de Hechos */}
                      {hechosText && (
                        <div className="rounded-xl border border-muted-foreground/10 bg-muted/30 p-2.5 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground/90">
                            <FileText className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
                            <span>Resumen de Hechos / Antecedentes</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                            {hechosText}
                          </p>
                        </div>
                      )}

                      {/* Antecedentes Jurídicos / Fundamentos */}
                      {antecedentesText && (
                        <div className="rounded-xl border border-muted-foreground/10 bg-emerald-500/5 p-2.5 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                            <BookOpen className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            <span>Antecedentes Jurídicos / Fundamentos</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                            {antecedentesText}
                          </p>
                        </div>
                      )}

                      {/* Legislación y Artículos Aplicados */}
                      {res.appliedLegislation && res.appliedLegislation.length > 0 && (
                        <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-2.5 space-y-1.5">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-purple-800 dark:text-purple-300">
                            <Scale className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400 shrink-0" />
                            <span>Legislación y Artículos Aplicados</span>
                          </div>
                          <div className="flex flex-wrap gap-1 pt-0.5">
                            {res.appliedLegislation.map((art, idx) => (
                              <Badge key={idx} variant="outline" className="bg-background/80 border-purple-300 dark:border-purple-800 text-purple-900 dark:text-purple-200 text-[11px] px-2 py-0.5 font-mono">
                                <Scale className="h-3 w-3 mr-1 text-purple-600 shrink-0" />
                                {art}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2.5 border-t pt-3 mt-3 text-xs text-muted-foreground">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          {res.court && (
                            <span className="flex items-center gap-1 shrink-0">
                              <Landmark className="h-3 w-3" /> {res.court}
                            </span>
                          )}
                          {res.date && (
                            <span className="flex items-center gap-1 shrink-0">
                              <Calendar className="h-3 w-3" /> {res.date}
                            </span>
                          )}
                        </div>
                      </div>

                      {resolutionUrl && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full gap-1.5 bg-primary/5 hover:bg-primary/10 text-primary border-primary/20 hover:border-primary/40 transition-colors"
                          onClick={() => window.open(resolutionUrl, '_blank', 'noopener,noreferrer')}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Ver Resolución Oficial</span>
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
